// Utility to convert notification objects into user-friendly messages

function formatTimeAgoOrDate(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  // older: show date like "25 Dec, 12:31 PM"
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function prettifyValue(val) {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (/^[a-z0-9_]+$/.test(trimmed) && trimmed.includes('_')) {
    return trimmed.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return trimmed;
}

export function formatNotificationForList(notif) {
  const type = notif?.type || "";
  const data = notif?.data || {};
  const createdAt = notif?.createdAt;
  const time = formatTimeAgoOrDate(createdAt);

  switch (type) {
    case "task.updated": {
      const changes = Array.isArray(data.changes) ? data.changes : [];
      const statusChange = changes.find((c) => (c && (c.field === "status")));
      let summary = `Task ${data.taskTitle || notif?.title || "updated"}`;
      if (statusChange && statusChange.newValue) summary = `Status changed to ${prettifyValue(statusChange.newValue)}`;
      else if (changes.length) summary = `${changes.length} change${changes.length === 1 ? "" : "s"} made`;

      return {
        title: "Task Updated",
        summary,
        time,
        ctaLabel: "View Task",
        ctaLink: notif?.link || (data?.taskId ? `/tasks/${data.taskId}` : `/home`),
      };
    }

    case "task.assigned":
      return {
        title: "Task Assigned",
        summary: `Task ${data.taskTitle || "untitled"} assigned to ${data.assignedToName || data.assignedTo?.name || "someone"}`,
        time,
        ctaLabel: "View Task",
        // prefer explicit link from notification; otherwise navigate to the task page
        // send users to the task page (not a creation flow)
        ctaLink: notif?.link || `/home/new-task`,
      };

    case "deal.assigned":
      return {
        title: "Deal Assigned",
        summary: `Deal ${data.dealTitle || "untitled"} assigned to ${data.assignedToName || data.assignedTo?.name || "someone"}${(data.assignedByName || data.actorName || data.assignedByUser?.name || data.actorUser?.name) ? ` by ${data.assignedByName || data.actorName || data.assignedByUser?.name || data.actorUser?.name}` : ""}`,
        time,
        ctaLabel: "View Deal",
        // prefer explicit link from notification; otherwise send to home dashboard
        ctaLink: notif?.link || `/home`,
      };

    case "task.created":
      return {
        title: "New Task Created",
        summary: `New task created: ${data.taskTitle || "untitled"} by ${data.actorName || data.createdByName || data.createdByUser?.name || "someone"}`,
        time,
        ctaLabel: "View Task",
        // prefer explicit link; otherwise send to task page
        ctaLink: notif?.link || `/home/new-task`,
      };

    case "member.logged_in":
      return {
        title: "Member Logged In",
        summary: `${data.memberName || "Someone"} logged in`,
        time,
        ctaLabel: null,
        ctaLink: null,
      };

    case "deal.updated": {
      return {
        title: "Deal Updated",
        summary: `Deal updated: ${data.title || "untitled"}${(data.actorName || data.updatedByName) ? ` by ${data.actorName || data.updatedByName}` : ""}`,
        time,
        ctaLabel: "View Deal",
        ctaLink: notif?.link || `/home`,
      };
    }

    case "stage.created": {
      return {
        title: notif?.title || "New Stage Added",
        summary: notif?.body || `A new stage was added to your pipeline.`,
        time,
        ctaLabel: "View Pipeline",
        ctaLink: notif?.link || `/home`,
      };
    }

    case "task.deleted":
      return {
        title: "Task Deleted",
        summary: `Task ${data.taskTitle || notif?.title || "deleted"} by ${data.actorName || data.deletedByName || data.deletedByUser?.name || "someone"}`,
        time,
        ctaLabel: null,
        ctaLink: null,
      };
    case "stage.added":
      return {
        title: "Stage Added",
        summary: `A new stage was added to pipeline ${data.pipelineName || "-"}`,
        time,
        ctaLabel: "View Pipeline",
        ctaLink: notif?.link || `/home`,
      };

    default:
      return {
        title: notif?.title || "Notification",
        summary: notif?.body || notif?.message || notif?.type || "You have a new notification",
        time,
        ctaLabel: null,
        ctaLink: notif?.link || null,
      };
  }
}

export function formatNotificationForDetail(notif) {
  const type = notif?.type || "";
  const data = notif?.data || {};
  const base = { title: notif?.title || "Notification", rows: [], ctaLabel: null, ctaLink: null };

  switch (type) {
    case "task.assigned":
      base.title = "Task Assigned";
      base.rows.push({ label: "Task", value: data.taskTitle || "-" });
      base.rows.push({ label: "Assigned by", value: data.assignedByName || data.actorName || data.assignedByUser?.name || data.actorUser?.name || data.assignedBy || data.actorId || "-" });
      base.rows.push({ label: "Assigned to", value: data.assignedToName || data.assignedTo?.name || "-" });
      base.ctaLabel = "View Task";
      base.ctaLink = notif?.link || (data.taskId ? `/home/new-task` : null);
      return base;

    case "deal.assigned":
      base.title = "Deal Assigned";
      base.rows.push({ label: "Deal", value: data.dealTitle || "-" });
      base.rows.push({ label: "Assigned by", value: data.assignedByName || data.actorName || data.assignedByUser?.name || data.actorUser?.name || data.assignedBy || data.actorId || "-" });
      base.rows.push({ label: "Assigned to", value: data.assignedToName || data.assignedTo?.name || "-" });
      base.ctaLabel = "View Deal";
      base.ctaLink = notif?.link || (data.dealId ? `/home` : null);
      return base;

    case "task.created":
      base.title = "New Task Created";
      base.rows.push({ label: "Task", value: data.taskTitle || "-" });
      base.rows.push({ label: "Created by", value: data.actorName || data.createdByName || data.createdByUser?.name || data.createdBy || "-" });
      base.ctaLabel = "View Task";
      base.ctaLink = notif?.link || (data.taskId ? `/home/new-task` : null);
      return base;
    case "task.deleted":
      base.title = "Task Deleted";
      base.rows.push({ label: "Task", value: data.taskTitle || "-" });
      base.rows.push({ label: "Deleted by", value: data.actorName || data.deletedByName || data.deletedByUser?.name || data.deletedBy || "-" });
      return base;
    case "member.logged_in":
      base.title = "Member Logged In";
      base.rows.push({ label: "Member", value: data.memberName || "-" });
      return base;

    case "deal.updated":
      base.title = "Deal Updated";
      base.rows.push({ label: "Deal", value: data.title || "-" });
      // show who performed the update when available
      base.rows.push({ label: "Updated by", value: data.actorName || data.updatedByName || data.actorUser?.name || data.updatedByUser?.name || data.updatedBy || data.actorId || "-" });
      // summarize changes without exposing raw JSON or field names
      const changes = Array.isArray(data.changes) ? data.changes : [];
      const changeMsgs = [];
      for (const c of changes) {
        const f = (c && c.field) || c?.key || "";
        if (!f) continue;
        if (f.includes("clientCompanies")) changeMsgs.push("Client company updated");
        else if (f.includes("pipelineId")) changeMsgs.push("Pipeline changed");
        else if (f.includes("stageId") || f.includes("assignedStageId")) changeMsgs.push("Deal stage updated");
        else changeMsgs.push("Details updated");
      }
      // dedupe
      base.changeList = Array.from(new Set(changeMsgs));
      base.ctaLabel = "View Deal";
      base.ctaLink = notif?.link || (data.dealId ? `/home` : null);
      return base;

    case "task.updated": {
      base.title = "Task Updated";
      base.rows.push({ label: "Task", value: data.taskTitle || "-" });
      base.rows.push({ label: "Updated by", value: data.actorName || data.updatedByName || data.updatedByUser?.name || data.updatedBy || data.actorId || "-" });
      const tChanges = Array.isArray(data.changes) ? data.changes : [];
      const tChangeMsgs = [];
      for (const c of tChanges) {
        const f = (c && c.field) || c?.key || "";
        if (!f) continue;
        if (f.includes("status")) tChangeMsgs.push(`Status changed to ${prettifyValue(c.newValue || "-")}`);
        else if (f.includes("startedAt")) tChangeMsgs.push("Task started");
        else if (f.includes("completedAt")) tChangeMsgs.push("Task completed");
        else if (f.includes("reminderAt")) tChangeMsgs.push("Reminder updated");
        else tChangeMsgs.push("Details updated");
      }
      base.changeList = Array.from(new Set(tChangeMsgs));
      base.ctaLabel = "View Task";
      base.ctaLink = notif?.link || (data.taskId ? `/home/new-task` : null);
      return base;
    }

    case "stage.created": {
      base.title = notif?.title || "New Stage Added";
      // base.rows.push({ label: "Pipeline", value: data.pipelineId || "-" });
      base.rows.push({ label: "Stage Name", value: data.stageName || "-" });
      // base.rows.push({ label: "Stage ID", value: data.stageId || "-" });
      base.ctaLabel = "View Pipeline";
      base.ctaLink = notif?.link || `/home`;
      return base;
    }
    case "stage.added":
      base.title = "Stage Added";
      base.rows.push({ label: "Pipeline", value: data.pipelineName || "-" });
      base.rows.push({ label: "Stage Name", value: data.stageName || "-" });
      base.ctaLabel = "View Pipeline";
      base.ctaLink = notif?.link || `/home`;
      return base;
    default:
      base.title = notif?.title || "Notification";
      if (notif?.body) base.rows.push({ label: "Message", value: notif.body });
      return base;
  }
}


const notificationFormatter = {
  formatNotificationForList,
  formatNotificationForDetail,
};

export default notificationFormatter;
